import { useState } from "react";
import ReactECharts from "echarts-for-react";
import moment, { type Moment } from "moment/moment";
import {
  IconAlertTriangle,
  IconBuildingBank,
  IconCalculator,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconCurrencyEuro,
  IconReceipt,
  IconTrendingUp,
} from "@tabler/icons-react";
import Card from "../UI/Card";
import DashboardCard from "../UI/DashboardCard";
import Button from "../UI/Button";
import Callout from "../UI/Callout";
import Text from "../UI/Text";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../UI/SelectStandalone";
import { Table } from "../UI/Table";
import Tooltip from "../UI/Tooltip";
import FilterBar from "../UI/FilterBar/FilterBar";
import { FilterSelectSection } from "../UI/FilterBar/FilterSection";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";
// @ts-expect-error pure js
import useProfileBailleurs from "../../hooks/use-profile-bailleurs";
// @ts-expect-error pure js
import useDebouncedValue from "../../hooks/use-debounced-value";
import useProfileFilter from "../../hooks/use-profile-filter";
import useStatsRentabilite from "../../hooks/use-stats-rentabilite";
import IconButton from "../UI/IconButton";
import LoyersPercusCharts from "./LoyersPercusCharts";
import RentabiliteDetailDialog from "./RentabiliteDetailDialog";
import { warningMessage } from "./rentabiliteWarnings";
import useDismissedAlerts from "./useDismissedAlerts";
import type { RentabiliteBien } from "../../models/statsRentabilite";
import styles from "./Rentabilite.module.css";

const DATE_FORMAT = "YYYY-MM-DD";
const ALL_YEARS_FILTER = "all";
const BIENS_INCOMPLETS_ALERT = "biens_incomplets";

const BIEN_WARNING_LABELS: Record<string, string> = {
  prix_achat_manquant: "sans prix d'achat",
  aucun_pret: "sans prêt rattaché",
  pret_sans_echeancier: "avec un prêt sans échéancier",
  aucune_depense: "sans aucune dépense affectée",
  prix_achat_mixte: "avec un prix d'achat en double (immeuble + biens)",
};

const WARNING_TITLES: Record<string, string> = {
  charges_non_affectees: "Dépenses non affectées",
  transactions_non_affectees: "Transactions bancaires non affectées",
};

function percentFormatter(value: number | null): string {
  if (value === null) {
    return "—";
  }
  return `${value.toLocaleString("fr-FR")} %`;
}

function BienWarnings({ bien }: { bien: RentabiliteBien }) {
  if (bien.warnings.length === 0) {
    return null;
  }
  return (
    <Tooltip
      content={
        <ul className={styles.warningList}>
          {bien.warnings.map((warning) => (
            <li key={warning.code}>{warningMessage(warning)}</li>
          ))}
        </ul>
      }
    >
      <IconAlertTriangle size={18} className={styles.warningIcon} />
    </Tooltip>
  );
}

interface BienRowProps {
  bien: RentabiliteBien;
  isSubRow?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  onShowDetail: () => void;
}

function BienRow({
  bien,
  isSubRow = false,
  expanded = false,
  onToggle,
  onShowDetail,
}: BienRowProps) {
  const expandable = bien.is_folder && bien.details.length > 0;
  const Chevron = expanded ? IconChevronDown : IconChevronRight;
  return (
    <Table.Row
      className={
        expandable ? styles.folderRow : isSubRow ? styles.subRow : undefined
      }
      onClick={expandable ? onToggle : undefined}
      aria-expanded={expandable ? expanded : undefined}
    >
      <Table.Cell className={isSubRow ? styles.subRowName : undefined}>
        {expandable && <Chevron size={16} className={styles.chevron} />}
        {isSubRow ? (
          <Text as="span" color="muted">
            {bien.nom}
          </Text>
        ) : (
          bien.nom
        )}
        {bien.is_folder && (
          <Text as="span" color="muted">
            {" "}
            (immeuble)
          </Text>
        )}
      </Table.Cell>
      <Table.Cell align="right">
        {currencyFormatter(bien.cashflow_mensuel)}
      </Table.Cell>
      <Table.Cell align="right">
        {percentFormatter(bien.rentabilite_brute)}
      </Table.Cell>
      <Table.Cell align="right">
        {percentFormatter(bien.rentabilite_nette)}
      </Table.Cell>
      <Table.Cell align="right">
        {bien.capital_restant_du !== null
          ? currencyFormatter(bien.capital_restant_du)
          : "—"}
      </Table.Cell>
      <Table.Cell align="right">
        {bien.taux_occupation !== null
          ? `${bien.taux_occupation.toLocaleString("fr-FR")} %`
          : "—"}
      </Table.Cell>
      <Table.Cell align="center">
        <BienWarnings bien={bien} />
      </Table.Cell>
      <Table.Cell align="center">
        <IconButton
          icon={IconCalculator}
          label="Détail du calcul"
          size="sm"
          variant="plain"
          onClick={(e) => {
            e.stopPropagation();
            onShowDetail();
          }}
        />
      </Table.Cell>
    </Table.Row>
  );
}

export default function Rentabilite() {
  const { properties } = useProperties();
  const { profileBailleurs } = useProfileBailleurs();
  const [debut, setDebut] = useState<Moment | null>(moment().startOf("year"));
  const [fin, setFin] = useState(moment().endOf("year"));
  const deferredDebut = useDebouncedValue(debut);
  const deferredFin = useDebouncedValue(fin);
  const [search, setSearch] = useState("");
  const [propertyFilter, setPropertyFilter] = useState<number[]>([]);
  const [profileBailleurFilter, setProfileBailleurFilter] = useState<number[]>(
    [],
  );
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(
    new Set(),
  );
  const [detailBien, setDetailBien] = useState<RentabiliteBien | null>(null);

  function toggleFolder(id: number) {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }
  const { selectedProfileIds } = useProfileFilter();
  const effectiveProfileBailleurFilter =
    profileBailleurFilter.length > 0
      ? profileBailleurFilter
      : selectedProfileIds;

  const { rentabilite, isLoading } = useStatsRentabilite(
    deferredDebut?.format(DATE_FORMAT),
    (deferredFin ?? moment()).format(DATE_FORMAT),
    propertyFilter,
    effectiveProfileBailleurFilter,
  );

  const years = Array.from({ length: 5 }, (_, i) => moment().year() - i);
  const selectedYear = debut?.year() ?? null;

  function selectYear(value: string) {
    if (value === ALL_YEARS_FILTER) {
      setDebut(null);
      setFin(moment());
    } else {
      setDebut(moment().year(Number(value)).startOf("year"));
      setFin(moment().year(Number(value)).endOf("year"));
    }
  }

  const canGoPrev =
    selectedYear !== null && selectedYear > years[years.length - 1];
  const canGoNext = selectedYear !== null && selectedYear < years[0];

  const activeFiltersCount =
    (propertyFilter.length > 0 ? 1 : 0) +
    (profileBailleurFilter.length > 0 ? 1 : 0);

  function clearFilters() {
    setPropertyFilter([]);
    setProfileBailleurFilter([]);
  }

  const searchLower = search.trim().toLowerCase();
  const allBiens = rentabilite?.biens ?? [];
  const biens = searchLower
    ? allBiens.filter(
        (b) =>
          b.nom.toLowerCase().includes(searchLower) ||
          b.details.some((d) => d.nom.toLowerCase().includes(searchLower)),
      )
    : allBiens;
  const nbMois = rentabilite?.nb_mois ?? 1;

  const bienWarningCounts: Record<string, number> = {};
  for (const bien of biens) {
    for (const warning of bien.warnings) {
      bienWarningCounts[warning.code] =
        (bienWarningCounts[warning.code] ?? 0) + 1;
    }
  }
  const bienWarningSummary = Object.entries(BIEN_WARNING_LABELS)
    .filter(([code]) => bienWarningCounts[code])
    .map(([code, label]) => `${bienWarningCounts[code]} bien(s) ${label}`);

  const {
    isDismissed: isAlertDismissed,
    dismiss: dismissAlert,
    restore: restoreAlerts,
  } = useDismissedAlerts(
    selectedYear !== null ? String(selectedYear) : ALL_YEARS_FILTER,
  );
  const alerts: {
    code: string;
    variant: "warning" | "info";
    title: string;
    message: string;
  }[] = [
    ...(rentabilite?.warnings ?? []).map((warning) => ({
      code: warning.code,
      variant: "warning" as const,
      title: WARNING_TITLES[warning.code],
      message: warningMessage(warning),
    })),
    ...(bienWarningSummary.length > 0
      ? [
          {
            code: BIENS_INCOMPLETS_ALERT,
            variant: "info" as const,
            title: "Données incomplètes sur certains biens",
            message: `${bienWarningSummary.join(
              " · ",
            )}. Le détail est dans la colonne Alertes du tableau (survolez l'icône ⚠️).`,
          },
        ]
      : []),
  ];
  const visibleAlerts = alerts.filter((alert) => !isAlertDismissed(alert.code));
  const hiddenAlertsCount = alerts.length - visibleAlerts.length;
  const totalLoyers = biens.reduce((acc, b) => acc + b.loyers, 0);
  const totalDepenses = biens.reduce((acc, b) => acc + b.depenses, 0);
  const totalPret = biens.reduce((acc, b) => acc + b.pret, 0);
  const totalCashflow = biens.reduce((acc, b) => acc + b.cashflow, 0);

  const months = biens[0]?.par_mois.map((m) => m.mois) ?? [];
  const loyersParMois = months.map((_, i) =>
    Math.round(biens.reduce((acc, b) => acc + (b.par_mois[i]?.loyers ?? 0), 0)),
  );
  const sortiesParMois = months.map((_, i) =>
    Math.round(
      -biens.reduce(
        (acc, b) =>
          acc + (b.par_mois[i]?.depenses ?? 0) + (b.par_mois[i]?.pret ?? 0),
        0,
      ),
    ),
  );
  const cashflowParMois = months.map(
    (_, i) => loyersParMois[i] + sortiesParMois[i],
  );

  return (
    <div className={styles.page}>
      <Card className={styles.stickyTop}>
        <FilterBar
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Rechercher un bien"
          activeFiltersCount={activeFiltersCount}
          onClearFilters={clearFilters}
          leftActions={
            <div className={styles.yearSelector}>
              <IconButton
                label="Année précédente"
                onClick={() =>
                  selectYear(String((selectedYear ?? moment().year()) - 1))
                }
                disabled={!canGoPrev}
                icon={IconChevronLeft}
                variant="plain"
              />
              <ReactSelectStandalone
                onChange={(option: { value: string }) =>
                  selectYear(option.value)
                }
                value={{
                  value:
                    selectedYear !== null
                      ? String(selectedYear)
                      : ALL_YEARS_FILTER,
                  label: selectedYear !== null ? String(selectedYear) : "Tous",
                }}
                options={[
                  ...years.map((year) => ({
                    value: String(year),
                    label: String(year),
                  })),
                  { value: ALL_YEARS_FILTER, label: "Tous" },
                ]}
                className={styles.yearSelect}
              />
              <IconButton
                label="Année suivante"
                onClick={() =>
                  selectYear(String((selectedYear ?? moment().year()) + 1))
                }
                disabled={!canGoNext}
                icon={IconChevronRight}
                variant="plain"
              />
            </div>
          }
        >
          <FilterSelectSection
            title="Biens"
            options={properties.map((p: { id: number; nom: string }) => ({
              value: String(p.id),
              label: p.nom,
            }))}
            values={propertyFilter.map(String)}
            onChange={(values) => setPropertyFilter(values.map(Number))}
            defaultOpen
          />
          <FilterSelectSection
            title="Profil bailleur"
            options={profileBailleurs.map(
              (p: { id: number; nom_profile: string }) => ({
                value: String(p.id),
                label: p.nom_profile,
              }),
            )}
            values={profileBailleurFilter.map(String)}
            onChange={(values) => setProfileBailleurFilter(values.map(Number))}
          />
        </FilterBar>
      </Card>

      {visibleAlerts.map((alert) => (
        <Callout
          key={alert.code}
          variant={alert.variant}
          title={alert.title}
          className={alert.variant === "warning" ? styles.alert : undefined}
          onClose={() => dismissAlert(alert.code)}
        >
          {alert.message}
        </Callout>
      ))}
      {hiddenAlertsCount > 0 && (
        <Text color="muted" className={styles.hiddenAlerts}>
          {hiddenAlertsCount} alerte{hiddenAlertsCount > 1 ? "s" : ""} masquée
          {hiddenAlertsCount > 1 ? "s" : ""}{" "}
          {selectedYear !== null
            ? `pour ${selectedYear}`
            : "sur toutes les années"}
          {" · "}
          <Button variant="tertiary" onClick={restoreAlerts}>
            Afficher
          </Button>
        </Text>
      )}

      <div className={styles.kpiRow}>
        <DashboardCard
          title="Cashflow"
          value={currencyFormatter(totalCashflow)}
          subtitle={`soit ${currencyFormatter(totalCashflow / nbMois)} / mois`}
          icon={IconTrendingUp}
          iconColor={totalCashflow >= 0 ? "success" : "danger"}
        />
        <DashboardCard
          title="Loyers encaissés"
          value={currencyFormatter(totalLoyers)}
          subtitle={`sur ${nbMois} mois`}
          icon={IconCurrencyEuro}
        />
        <DashboardCard
          title="Dépenses"
          value={currencyFormatter(totalDepenses)}
          subtitle="charges, taxes, assurances… (hors travaux)"
          icon={IconReceipt}
        />
        <DashboardCard
          title="Mensualités de prêt"
          value={currencyFormatter(totalPret)}
          subtitle="capital, intérêts, assurance"
          icon={IconBuildingBank}
        />
      </div>

      <Card>
        <ReactECharts
          option={{
            title: { text: "Cashflow mensuel", left: "center" },
            tooltip: { trigger: "axis" },
            xAxis: {
              type: "category",
              data: months,
              axisLabel: { rotate: 45 },
            },
            yAxis: { type: "value", name: "Montant (€)" },
            series: [
              {
                name: "Loyers encaissés",
                data: loyersParMois,
                type: "bar",
                stack: "total",
                itemStyle: { color: "#5470C6" },
              },
              {
                name: "Dépenses + prêt",
                data: sortiesParMois,
                type: "bar",
                stack: "total",
                itemStyle: { color: "rgba(245,12,20,0.66)" },
              },
              {
                name: "Cashflow",
                data: cashflowParMois,
                type: "line",
                itemStyle: { color: "#000000" },
              },
            ],
          }}
          style={{ height: 300, width: "100%" }}
        />
      </Card>

      <Card>
        <Table variant="card">
          <Table.Header>
            <Table.Column>Bien</Table.Column>
            <Table.Column align="right">Cashflow / mois</Table.Column>
            <Table.Column align="right">Renta. brute</Table.Column>
            <Table.Column align="right">Renta. nette</Table.Column>
            <Table.Column align="right">Capital restant dû</Table.Column>
            <Table.Column align="right">Occupation</Table.Column>
            <Table.Column align="center">Alertes</Table.Column>
            <Table.Column align="center">Détail</Table.Column>
          </Table.Header>
          <Table.Body>
            {biens.flatMap((bien) => {
              const key = `${bien.is_folder ? "f" : "b"}${bien.id}`;
              const expanded = expandedFolders.has(bien.id);
              return [
                <BienRow
                  key={key}
                  bien={bien}
                  expanded={expanded}
                  onToggle={() => toggleFolder(bien.id)}
                  onShowDetail={() => setDetailBien(bien)}
                />,
                ...(bien.is_folder && expanded
                  ? bien.details.map((detail) => (
                      <BienRow
                        key={`${key}-${detail.id}`}
                        bien={detail}
                        isSubRow
                        onShowDetail={() => setDetailBien(detail)}
                      />
                    ))
                  : []),
              ];
            })}
          </Table.Body>
        </Table>
        {!isLoading && biens.length === 0 && (
          <Text color="muted" className={styles.emptyState}>
            Aucun bien sur la période sélectionnée
          </Text>
        )}
      </Card>

      <LoyersPercusCharts
        debut={deferredDebut?.format(DATE_FORMAT)}
        fin={(deferredFin ?? moment()).format(DATE_FORMAT)}
        propertyIds={propertyFilter}
        profileBailleurIds={effectiveProfileBailleurFilter}
      />

      {rentabilite && rentabilite.depenses_par_nature.length > 0 && (
        <Card>
          <ReactECharts
            option={{
              title: { text: "Dépenses par nature", left: "center" },
              tooltip: {
                trigger: "item",
                formatter: (params: { name: string; value: number }) =>
                  `${params.name} : ${currencyFormatter(params.value)}`,
              },
              series: [
                {
                  type: "pie",
                  radius: ["40%", "70%"],
                  avoidLabelOverlap: false,
                  label: { show: false, position: "center" },
                  emphasis: {
                    label: { show: true, fontSize: 16, fontWeight: "bold" },
                  },
                  labelLine: { show: false },
                  data: rentabilite.depenses_par_nature.map((d) => ({
                    name: d.label,
                    value: d.montant,
                  })),
                },
              ],
            }}
            style={{ height: 300, width: "100%" }}
          />
        </Card>
      )}

      <RentabiliteDetailDialog
        bien={detailBien}
        nbMois={nbMois}
        debut={rentabilite?.debut}
        fin={rentabilite?.fin}
        onOpenChange={(open) => {
          if (!open) {
            setDetailBien(null);
          }
        }}
      />
    </div>
  );
}
